import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  Box,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Inventory as PackageIcon,
  Description as TemplateIcon
} from '@mui/icons-material';
import { useAuth } from '../../features/auth/context/AuthContext';
import notificationService from '../../features/notifications/services/notificationService';
import SettingsModal from '../../features/settings/components/SettingsModal';

const TopBar = ({ onSidebarToggle, sidebarOpen, isMobile }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notifMenuAnchor, setNotifMenuAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('profile');

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  // Load notifications
  const loadNotifications = async () => {
    setLoading(true);
    const result = await notificationService.getAll({ skip: 0, limit: 5, is_read: false });
    if (result.success) {
      setNotifications(result.data || []);
      setUnreadCount(result.unread_count || 0);
    }
    setLoading(false);
  };

  // Load unread count
  const loadUnreadCount = async () => {
    const result = await notificationService.getUnreadCount();
    if (result.success) {
      setUnreadCount(result.count || 0);
    }
  };

  // Load on mount and every 30 seconds
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000); // Refresh count every 30 seconds
    
    return () => clearInterval(interval);
  }, []);

  const handleNotifMenuOpen = async (event) => {
    setNotifMenuAnchor(event.currentTarget);
    await loadNotifications(); // Refresh when opening
  };

  const handleNotifMenuClose = () => {
    setNotifMenuAnchor(null);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already read
    if (!notification.is_read) {
      await notificationService.markAsRead(notification.id);
      setUnreadCount(Math.max(0, unreadCount - 1));
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
    }
    handleNotifMenuClose();
    // Navigate to notifications page
    navigate('/notifications');
  };

  const handleViewAllNotifications = () => {
    handleNotifMenuClose();
    navigate('/notifications');
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleSettings = () => {
    setSettingsInitialTab('profile');
    setSettingsModalOpen(true);
    handleUserMenuClose();
  };

  const handleQuotePackages = () => {
    navigate('/quote-packages');
    handleUserMenuClose();
  };

  const handleServiceTemplates = () => {
    navigate('/service-templates');
    handleUserMenuClose();
  };

  return (
    <>
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: theme.palette.primary.main,
          boxShadow: 1
        }}
      >
        <Toolbar>
          {/* Menu Button - Mobile Only */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              onClick={onSidebarToggle}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Desktop Menu Button */}
          {!isMobile && (
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              onClick={onSidebarToggle}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/main_logo.png`}
              alt="BillMaster Logo"
              sx={{
                width: 32,
                height: 32,
                objectFit: 'contain'
              }}
            />
            <Typography variant="h6" noWrap>
              BillMaster
            </Typography>
          </Box>

          {/* Notifications */}
          <IconButton
            color="inherit"
            aria-label="notifications"
            onClick={handleNotifMenuOpen}
            sx={{ mr: 1 }}
          >
            <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Avatar */}
          <IconButton
            color="inherit"
            aria-label="user menu"
            onClick={handleUserMenuOpen}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                backgroundColor: theme.palette.secondary.main,
                fontSize: '14px'
              }}
            >
              {user?.username?.substring(0, 2)?.toUpperCase() || 'AD'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 180,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleQuotePackages}>
          <ListItemIcon>
            <PackageIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Πακέτα Προσφορών</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleServiceTemplates}>
          <ListItemIcon>
            <TemplateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Πρότυπα Υπηρεσιών</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Ρυθμίσεις</ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Αποσύνδεση</ListItemText>
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notifMenuAnchor}
        open={Boolean(notifMenuAnchor)}
        onClose={handleNotifMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 350,
            maxHeight: 500,
            maxWidth: 400,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Ειδοποιήσεις
          </Typography>
        </Box>
        
        {loading ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Φόρτωση...
            </Typography>
          </MenuItem>
        ) : notifications.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Δεν υπάρχουν νέες ειδοποιήσεις
            </Typography>
          </MenuItem>
        ) : (
          notifications.map((notification) => (
            <MenuItem
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Typography 
                  variant="body2" 
                  fontWeight={notification.is_read ? 'normal' : 'bold'}
                  sx={{ mb: 0.5 }}
                >
                  {notification.title}
                </Typography>
                {notification.message && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {notification.message.length > 60 
                      ? `${notification.message.substring(0, 60)}...` 
                      : notification.message}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {new Date(notification.created_at).toLocaleDateString('el-GR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
        
        {notifications.length > 0 && (
          <>
            <Divider />
            <MenuItem onClick={handleViewAllNotifications}>
              <Button size="small" fullWidth sx={{ justifyContent: 'center' }}>
                Προβολή όλων
              </Button>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Settings Modal */}
      <SettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        initialTab={settingsInitialTab}
      />
    </>
  );
};

export default TopBar;